import { extractCouponId, extractCouponSportId } from 'modules/cms-sport-coupon/utils.js';

/** * Utility functions for CMS coupons list.
 *
 * @namespace CmsCouponsListUtils
 * @memberof Mojito.Modules.CmsCouponsList
 */

/**
 * Resolves CMS coupon group metadata and events for the given subscription ids.
 *
 * @param {Array<string>} [groupIds] - CMS coupon event group subscription ids.
 * @param {object} eventGroups - Event groups keyed by subscription id.
 * @param {object} eventGroupNames - Display names keyed by subscription id.
 * @param {object} [eventGroupTitles = {}] - Optional titles keyed by subscription id.
 *
 * @returns {Array<object>} Coupon group info objects with eventGroupId, id, sportId, name, title and events.
 *
 * @function Mojito.Modules.CmsCouponsList.CmsCouponsListUtils#resolveCouponGroupInfos
 */
export const resolveCouponGroupInfos = (
    groupIds,
    eventGroups,
    eventGroupNames,
    eventGroupTitles = {}
) => {
    const ids = groupIds ?? [];
    return ids
        .filter(id => eventGroups[id])
        .map(eventGroupId => ({
            eventGroupId,
            id: extractCouponId(eventGroupId),
            sportId: extractCouponSportId(eventGroupId),
            name: eventGroupNames[eventGroupId],
            title: eventGroupTitles[eventGroupId],
            events: eventGroups[eventGroupId].flatMap(group => group.events),
        }));
};

/**
 * Builds CMS coupon menu link items from coupon group subscription data.
 *
 * @param {Array<string>} [groupIds] - CMS coupon event group subscription ids.
 * @param {object} eventGroups - Event groups keyed by subscription id.
 * @param {object} eventGroupNames - Display names keyed by subscription id.
 * @param {object} [eventGroupTitles] - Optional titles keyed by subscription id.
 *
 * @returns {Array<object>} Menu items with id, data and sportId for coupons that have a name and events.
 *
 * @function Mojito.Modules.CmsCouponsList.CmsCouponsListUtils#buildCmsCouponLinks
 */
export const buildCmsCouponLinks = (groupIds, eventGroups, eventGroupNames, eventGroupTitles) => {
    return resolveCouponGroupInfos(groupIds, eventGroups, eventGroupNames, eventGroupTitles)
        .filter(({ name, events }) => !!name && events.length > 0)
        .map(({ id, sportId, name, title }) => ({
            id,
            data: title || name,
            sportId,
        }));
};
